import type { MomentInput } from 'moment';
import { ProjectPhase } from 'api/project';
import { Sprint as SprintType } from 'api/sprint-planning/types';
import { extractId } from 'data/projects/phase-entity';
import { Task } from 'data/tasks';
import {
  DateShiftUpdates,
  DependencyTypesEnum,
  EntityType,
  List,
} from 'utils/date-shift';

export const getPhaseList = (phases: ProjectPhase[]): List => {
  const result: List = {};

  phases.forEach(
    ({
      projectPhaseId,
      projectPhaseName,
      startDate,
      dueDate,
      entityOrder,
      tasks,
      dependentProjectPhases,
      project,
    }) => {
      result[projectPhaseId] = {
        depth: 0,
        id: projectPhaseId,
        name: projectPhaseName,
        type: EntityType.Phase,
        startDate,
        dueDate,
        project: project.projectId,
        children:
          entityOrder?.map(extractId) ??
          tasks.map((_task) => _task.taskId ?? _task),
        dependentOn: dependentProjectPhases?.map(
          ({ projectPhaseId: dPhaseId, dependencyType, lag }) => ({
            id: dPhaseId,
            dependencyType: dependencyType
              ? DependencyTypesEnum[dependencyType]
              : 2,
            lag: lag ?? null,
          }),
        ),
      };

      dependentProjectPhases?.forEach(
        ({ projectPhaseId: dPhaseId, dependencyType, lag }) => {
          result[dPhaseId] = {
            ...(result[dPhaseId] ?? {}),
            dependencyFor: [
              ...(result[dPhaseId]?.dependencyFor ?? []),
              {
                id: projectPhaseId,
                dependencyType: dependencyType
                  ? DependencyTypesEnum[dependencyType]
                  : 2,
                lag: lag ?? null,
              },
            ],
          };
        },
      );
    },
  );

  return result;
};

export const getTaskList = (tasks: Task[], phaseList: List): List => {
  const result: List = {};

  tasks.forEach(
    ({
      taskId,
      startDate,
      dueDate,
      subTasks,
      dependentTasks,
      parentTask,
      projectPhase,
      taskName,
      private: isPrivate,
      project,
    }) => {
      result[taskId] = {
        ...(result[taskId] ?? {}),
        depth: 1,
        name: taskName,
        id: taskId,
        type: EntityType.Task,
        hasStartDate: !!startDate,
        hasDueDate: !!dueDate,
        startDate,
        dueDate,
        project,
        children: subTasks?.map((_task) => _task),
        parent: parentTask ?? projectPhase?.projectPhaseId,
        projectPhaseId: projectPhase?.projectPhaseId,
        isPrivate,
      };

      dependentTasks
        ?.filter(Boolean)
        .forEach(({ taskId: dTaskId, dependencyType, lag }) => {
          if (dependencyType !== undefined && dependencyType !== null) {
            result[dTaskId] = {
              ...(result[dTaskId] ?? {}),
              dependencyFor: [
                ...(result[dTaskId]?.dependencyFor ?? []),
                {
                  id: taskId,
                  dependencyType: dependencyType
                    ? DependencyTypesEnum[dependencyType]
                    : 2,
                  lag: lag ?? null,
                },
              ],
            };
            result[taskId] = {
              ...(result[taskId] ?? {}),
              dependentOn: [
                ...(result[taskId]?.dependentOn ?? []),
                {
                  id: dTaskId,
                  dependencyType: dependencyType
                    ? DependencyTypesEnum[dependencyType]
                    : 2,
                  lag: lag ?? null,
                },
              ],
            };
          }
        });
    },
  );

  const getDepth = (id: number) => {
    return !result[id]?.parent ? 0 : getDepth(result[id].parent) + 1;
  };

  const getStartDate = (id: number): MomentInput => {
    const startDateValue = (result[id] || phaseList[id])?.startDate;
    const parent = (result[id] || phaseList[id])?.parent;

    return startDateValue || (parent && getStartDate(parent));
  };

  Object.values(result).forEach((_task) => {
    result[_task.id] = {
      ..._task,
      depth: getDepth(_task.id),
    };
  });

  Object.values(result)
    .filter((_task) => !_task.startDate)
    .forEach((_task) => {
      result[_task.id] = {
        ..._task,
        startDate: _task.dueDate || getStartDate(_task.id),
      };
    });

  Object.values(result)
    .filter((_task) => !_task.dueDate)
    .forEach((_task) => {
      result[_task.id] = {
        ..._task,
        dueDate: _task.startDate,
      };
    });

  return result;
};

export const getSprintList = (sprints: SprintType[], phaseList: List): List => {
  const result: List = {};

  sprints.forEach(
    ({ sprintId, startDate, dueDate, projectPhase, sprintName }) => {
      result[sprintId] = {
        ...(result[sprintId] ?? {}),
        depth: 1,
        name: sprintName,
        id: sprintId,
        type: EntityType.Sprint,
        hasStartDate: !!startDate,
        hasDueDate: !!dueDate,
        startDate,
        dueDate,
        children: [],
        dependentOn: [],
        parent: projectPhase?.projectPhaseId,
        projectPhaseId: projectPhase?.projectPhaseId,
        project: projectPhase?.projectPhaseId
          ? phaseList[projectPhase.projectPhaseId]?.project
          : undefined,
      };
    },
  );

  const getStartDate = (id: number): MomentInput => {
    const startDateValue = (result[id] || phaseList[id])?.startDate;
    const parent = (result[id] || phaseList[id])?.parent;

    return startDateValue || (parent && getStartDate(parent));
  };

  const updatedResult = Object.values(result).reduce((acc, _sprint) => {
    let sprint = _sprint;

    if (!sprint.startDate) {
      const startDateValue = sprint.dueDate || getStartDate(sprint.id);

      sprint = { ...sprint, startDate: startDateValue };
    }

    if (!sprint.dueDate) {
      sprint = { ...sprint, dueDate: sprint.startDate };
    }

    acc[sprint.id] = sprint;

    return acc;
  }, {} as List);

  return updatedResult;
};

export const filterTaskUpdates = (
  taskUpdates: DateShiftUpdates,
  _tasks: Task[],
): DateShiftUpdates => {
  const filteredUpdates: DateShiftUpdates = {};

  Object.values(taskUpdates).forEach((update) => {
    const { id } = update;
    const _update = { ...update };
    const task = _tasks.find((_task) => _task.taskId === id);

    if (!task?.startDate && _update.startDate) {
      delete _update.startDate;
    }

    if (!task?.dueDate && _update.dueDate) {
      delete _update.dueDate;
    }

    filteredUpdates[id] = { ..._update };
  });

  return filteredUpdates;
};

export const getParentStartDate = (id: number, lookup: List): MomentInput => {
  const { startDate, parent } = lookup[id];

  return parent && lookup[parent].startDate
    ? lookup[parent].startDate
    : parent && getParentStartDate(parent, lookup)
    ? getParentStartDate(parent, lookup)
    : startDate;
};

export const normaliseTasks = (tasks): Task[] => {
  return tasks.map((task) => {
    const { subTasks, project, ...rest } = task;

    return {
      ...rest,
      subTasks: subTasks?.map(({ taskId }) => taskId),
      project: project.projectId,
    };
  });
};
